/**
 * Routes Manifest
 * Defines all application routes and their descriptions for the AI agent
 * so it can understand the app's navigation structure and suggest/perform navigation.
 */

export interface RouteInfo {
  path: string;
  name: string;
  description: string;
  category: 'auth' | 'dashboard' | 'records' | 'transactions' | 'entities' | 'accounts' | 'profile' | 'credits' | 'subscription' | 'other';
  requiresAuth: boolean;
  requiresRecord?: boolean;
  params?: Record<string, string>;
}

export const routesManifest: RouteInfo[] = [
  // Auth Pages
  {
    path: '/',
    name: 'Login',
    description: 'User login page - sign in with email and password or Google',
    category: 'auth',
    requiresAuth: false,
  },
  {
    path: '/login',
    name: 'Login',
    description: 'User login page - sign in with email and password or Google',
    category: 'auth',
    requiresAuth: false,
  },
  {
    path: '/signup',
    name: 'Sign Up',
    description: 'New user registration page - create a new account',
    category: 'auth',
    requiresAuth: false,
  },
  {
    path: '/forgot-password',
    name: 'Forgot Password',
    description: 'Password reset page - request password reset code and set new password',
    category: 'auth',
    requiresAuth: false,
  },
  {
    path: '/privacy-policy',
    name: 'Privacy Policy',
    description: 'Privacy policy information page',
    category: 'other',
    requiresAuth: false,
  },

  // Dashboard
  {
    path: '/dashboard',
    name: 'Dashboard',
    description: 'Main dashboard showing key performance indicators (KPIs), financial summary, and analytics',
    category: 'dashboard',
    requiresAuth: true,
  },

  // Records
  {
    path: '/records',
    name: 'Records',
    description: 'List of all accounting records (top-level containers for transactions). Users can create, edit, delete, join, import/export records, and manage permissions.',
    category: 'records',
    requiresAuth: true,
  },

  // Transactions
  {
    path: '/records/transactions',
    name: 'Transactions',
    description: 'List of all transactions within a selected record. Shows transaction details with actions, objects, media, quantities, and amounts. Supports filtering.',
    category: 'transactions',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/transactions/new',
    name: 'New Transaction',
    description: 'Create a new transaction within a selected record. Form includes date, action, object, indirect object, media, quantity, and total amount.',
    category: 'transactions',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/transactions/edit/:recordId/:transactionId',
    name: 'Edit Transaction',
    description: 'Edit an existing transaction. Parameters: recordId (the record ID), transactionId (the transaction ID to edit).',
    category: 'transactions',
    requiresAuth: true,
    requiresRecord: true,
    params: { recordId: 'number', transactionId: 'number' },
  },

  // Transaction Entities
  {
    path: '/records/transaction-entities',
    name: 'Transaction Entities',
    description: 'Manage transaction entities - actions (verbs like sold, bought), objects (items), indirect objects, and media (payment types). Supports CRUD operations.',
    category: 'entities',
    requiresAuth: true,
    requiresRecord: true,
  },

  // Accounts Overview
  {
    path: '/records/accounts',
    name: 'Accounts Overview',
    description: 'Accounts overview page - gateway to ledgers, stock valuation, trading accounts, P&L, and balance sheets.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },

  // Ledgers
  {
    path: '/records/accounts/ledgers',
    name: 'Ledger Accounts',
    description: 'List of all ledger accounts (entity-based financial summaries showing balances). Shows each entity with its balance amount, quantity, and type.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/accounts/ledgers/:entityId',
    name: 'Single Ledger',
    description: 'Detailed ledger view for a specific entity. Shows debit and credit transactions with date range filtering. Parameter: entityId.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
    params: { entityId: 'number' },
  },

  // Stock Valuation
  {
    path: '/records/accounts/stock-valuation',
    name: 'Stock Valuation Accounts',
    description: 'List of stock valuation accounts showing inventory/stock items with their current valuations.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/accounts/stock-valuation/:entityId',
    name: 'Single Stock Valuation',
    description: 'Detailed stock valuation for a specific item. Shows purchase/sale history with running balance. Parameter: entityId.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
    params: { entityId: 'number' },
  },
  {
    path: '/records/accounts/overall-stock-valuation',
    name: 'Overall Stock Valuation',
    description: 'Aggregated stock valuation across all stock items. Shows combined inventory position.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },

  // Trading Accounts
  {
    path: '/records/accounts/trading-accounts',
    name: 'Trading Accounts',
    description: 'List of trading accounts showing trading-related entities and their balances.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/accounts/trading-accounts/:entityId',
    name: 'Single Trading Account',
    description: 'Detailed trading account for a specific entity showing debit and credit breakdown. Parameter: entityId.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
    params: { entityId: 'number' },
  },
  {
    path: '/records/accounts/overall-trading-account',
    name: 'Overall Trading Account',
    description: 'Aggregated trading account showing combined debit and credit balances across all trading entities.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },

  // P&L and Balance Sheet
  {
    path: '/records/accounts/profit-loss',
    name: 'Profit & Loss Statement',
    description: 'Profit and Loss statement showing total debits and credits with net profit/loss calculation for a specified date range.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },
  {
    path: '/records/accounts/balance-sheets',
    name: 'Balance Sheet',
    description: 'Balance sheet showing assets, liabilities, and capital for a specified date range. Follows the accounting equation: Assets = Liabilities + Capital.',
    category: 'accounts',
    requiresAuth: true,
    requiresRecord: true,
  },

  // Profile
  {
    path: '/profile',
    name: 'Profile',
    description: 'User profile page - view and edit personal information, username, email, password, and profile image.',
    category: 'profile',
    requiresAuth: true,
  },

  // Credits
  {
    path: '/credit-points',
    name: 'Credit Points',
    description: 'View credit points balance and transaction history. Credits are in-app currency used for purchases and subscriptions.',
    category: 'credits',
    requiresAuth: true,
  },
  {
    path: '/credit/purchase',
    name: 'Purchase Credits',
    description: 'Purchase additional credit points using Flutterwave or Stripe payment gateways.',
    category: 'credits',
    requiresAuth: true,
  },

  // Subscription
  {
    path: '/subscription',
    name: 'Subscription',
    description: 'Manage subscription plans. View current subscription, available plans, and subscribe to new plans using credit points or payment gateways.',
    category: 'subscription',
    requiresAuth: true,
  },
  {
    path: '/subscription/success',
    name: 'Subscription Success',
    description: 'Payment/subscription success confirmation page shown after a successful subscription payment.',
    category: 'subscription',
    requiresAuth: true,
  },
];

export function getRouteByPath(path: string): RouteInfo | undefined {
  // Try exact match first
  const exact = routesManifest.find(r => r.path === path);
  if (exact) return exact;

  // Try pattern match for parameterized routes
  return routesManifest.find(r => {
    if (!r.params) return false;
    const routeParts = r.path.split('/');
    const inputParts = path.split('/');
    if (routeParts.length !== inputParts.length) return false;
    return routeParts.every((part, i) => {
      return part.startsWith(':') || part === inputParts[i];
    });
  });
}

export function getRoutesByCategory(category: RouteInfo['category']): RouteInfo[] {
  return routesManifest.filter(r => r.category === category);
}
